import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Phone, MapPin, Calendar, Send, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ContactNodixs = () => {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const navigate = useNavigate();

  
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } 
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulation validation simple
    setStatus('loading');
    setTimeout(() => setStatus('success'), 1200);
  };

  return (
   
    <section className="w-full bg-white py-16 px-6 font-inter border-t border-slate-100 mt-12">
      <div className="max-w-[1200px] mx-auto">
        
      
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
          className="mb-10"
        >
          <h2 className="text-[#0f172a] text-3xl md:text-4xl font-black mb-4 tracking-tight">
            Contactez l'équipe <span className="text-[#1e3a5f]">Nodixs</span>
          </h2>
          <p className="text-slate-500 text-sm md:text-base font-medium max-w-3xl leading-relaxed">
            Une question ou un projet en tête ? Notre équipe vous répond en moins de 24h.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-[320px,1fr] gap-10 items-start">
          
          {/* Sidebar - Les éléments apparaissent en cascade */}
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="space-y-6"
          >
            <motion.div variants={fadeInUp} className="space-y-5">
              <ContactInfoBox icon={<Mail size={16} />} label="EMAIL" value="contact@nodixs.com" sub="Sous 24h ouvrées." />
              <ContactInfoBox icon={<MapPin size={16} />} label="ADRESSE" value="41 rue chevalier toile 82400 valence" />
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-slate-50 p-5 rounded-2xl border border-slate-200 shadow-md">
              <div className="flex items-center gap-2 text-[#3b82f6] mb-3">
                <Calendar size={16} />
                <span className="text-[10px] font-black uppercase tracking-wider">Disponible</span>
              </div>
              <h4 className="text-[#0f172a] font-bold text-sm mb-1.5">Besoin d'un échange ?</h4>
              <button 
                onClick={() => navigate('/rdv')}
                className="w-full bg-[#1e3a5f] text-white py-2.5 rounded-xl text-[13px] font-bold flex items-center justify-center gap-2 hover:bg-[#0f172a] transition-all"
              >
                Prendre RDV
              </button>
            </motion.div>
          </motion.div>

          {/* Formulaire - Anime de son côté avec un léger délai */}
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              hidden: { opacity: 0, x: 10 },
              visible: { opacity: 1, x: 0, transition: { duration: 0.6, delay: 0.3 } }
            }}
            className="bg-slate-50 p-7 md:p-9 rounded-2xl border border-slate-200 shadow-md relative overflow-hidden"
          >
            <AnimatePresence mode="wait">
              {status === 'success' ? (
                <motion.div 
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center py-10 text-center"
                >
                  <CheckCircle2 size={32} className="text-green-500 mb-3" />
                  <h3 className="text-lg font-black text-[#0f172a]">Message envoyé !</h3>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InputGroup label="Nom complet" placeholder="Jean Dupont" />
                    <InputGroup label="Adresse Email" placeholder="jean@entreprise.com" />
                  </div>
                  <InputGroup label="Entreprise" placeholder="Nom de votre société" />
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[13px] font-bold text-slate-700">Message</label>
                    <textarea 
                      rows="4"
                      className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 transition-all text-sm resize-none"
                      placeholder="Comment pouvons-nous vous aider ?"
                    />
                  </div>
                  <div className="pt-2 flex flex-col md:flex-row items-center gap-5">
                    <button className="w-full md:w-auto bg-[#1e3a5f] text-white px-8 py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-[#0f172a] transition-all text-[14px]">
                      {status === 'loading' ? "Envoi..." : "Envoyer le message"} <Send size={15} />
                    </button>
                  </div>
                </form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ContactInfoBox = ({ icon, label, value, sub }) => (
  <div className="flex items-start gap-4">
    <div className="w-9 h-9 bg-blue-50 text-[#3b82f6] rounded-xl flex items-center justify-center shrink-0">
      {icon}
    </div>
    <div>
      <span className="block text-[10px] font-black text-slate-400 tracking-widest mb-0.5 uppercase">{label}</span>
      <span className="block text-[#0f172a] font-bold text-[14px] mb-0.5">{value}</span>
      <span className="block text-slate-400 text-[11px] font-medium leading-tight">{sub}</span>
    </div>
  </div>
);

const InputGroup = ({ label, placeholder }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[13px] font-bold text-slate-700">{label}</label>
    <input type="text" placeholder={placeholder} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 transition-all text-sm" />
  </div>
);

export default ContactNodixs;